
import logger from '../utils/logger';

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    logger.log('Notifications not supported');
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  logger.log('Notification permission:', permission);
  return permission;
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    logger.log('Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    logger.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    logger.error('Service Worker registration failed:', error);
    return null;
  }
};

// Get existing service worker registration
export const getServiceWorkerRegistration = async () => {
  if (!('serviceWorker' in navigator)) return null;
  return await navigator.serviceWorker.ready;
};

// Subscribe to push notifications
export const subscribeToPush = async () => {
  try {
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      throw new Error('No service worker registration');
    }

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Get VAPID public key from server
      const response = await fetch(`/api/push/vapid-public-key`, {
        credentials: 'include'
      });
      const { publicKey } = await response.json();

      // Subscribe with VAPID key
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }

    // Send subscription to server
    await saveSubscriptionToServer(subscription);
    
    logger.log('Push subscription:', subscription);
    return subscription;
  } catch (error) {
    logger.error('Failed to subscribe to push:', error);
    throw error;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async () => {
  try {
    const registration = await getServiceWorkerRegistration();
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;

    // Unsubscribe locally
    await subscription.unsubscribe();

    // Remove subscription from server
    await removeSubscriptionFromServer(subscription);

    logger.log('Unsubscribed from push');
    return true;
  } catch (error) {
    logger.error('Failed to unsubscribe from push:', error);
    return false;
  }
};

// Check if user is subscribed to push
export const isSubscribedToPush = async () => {
  try {
    const registration = await getServiceWorkerRegistration();
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

// Save subscription to server
const saveSubscriptionToServer = async (subscription, userId = null) => {
  // Try to get userId from localStorage if not provided
  if (!userId) {
    try {
      const storedUser = localStorage.getItem('xoclub_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        userId = user.id || user._id;
      }
    } catch (e) {
      logger.debug('No user found for push subscription');
    }
  }

  const response = await fetch(`/api/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      userId: userId
    })
  });

  if (!response.ok) {
    throw new Error('Failed to save subscription to server');
  }

  return response.json();
};

// Remove subscription from server
const removeSubscriptionFromServer = async (subscription) => {
  const response = await fetch(`/api/push/unsubscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      endpoint: subscription.endpoint
    })
  });

  return response.ok;
};

// Helper: Convert VAPID public key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Show a local notification (for testing)
export const showLocalNotification = async (title, options = {}) => {
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    logger.log('No service worker registration');
    return;
  }

  await registration.showNotification(title, {
    body: options.body || '',
    icon: options.icon || '/icons/icon.svg',
    badge: '/icons/icon.svg',
    tag: options.tag || 'xoclub-notification',
    data: options.data || {},
    vibrate: [100, 50, 100],
    ...options
  });
};
